/**
 * Chat utility functions for determining chat availability
 */

/**
 * Check if an event's group chat is currently available
 * Opens 24 hours before event starts, closes 24 hours after event ends
 * @param {Object} event - Event document with startsAt, endsAt, groupChat
 * @returns {boolean} - True if chat is available
 */
export function isEventChatAvailable(event) {
  if (!event?.groupChat?.enabled) {
    return false;
  }

  const now = new Date();
  const startTime = event.startsAt?.toDate ? event.startsAt.toDate() : new Date(event.startsAt);
  const endTime = event.endsAt?.toDate ? event.endsAt.toDate() : startTime;

  // Chat opens 24 hours before event starts
  const opensAt = new Date(startTime.getTime() - 24 * 60 * 60 * 1000);
  
  // Chat closes 24 hours after event ends (or 24h after start if no end time)
  const closesAt = new Date(endTime.getTime() + 24 * 60 * 60 * 1000);

  return now >= opensAt && now <= closesAt;
}

/**
 * Get the time when an event chat will open
 * @param {Object} event - Event document with startsAt
 * @returns {Date|null} - Date when chat opens, or null if not enabled
 */
export function getEventChatOpensAt(event) {
  if (!event?.groupChat?.enabled || !event.startsAt) {
    return null;
  }

  const startTime = event.startsAt?.toDate ? event.startsAt.toDate() : new Date(event.startsAt);
  return new Date(startTime.getTime() - 24 * 60 * 60 * 1000);
}

/**
 * Get the time when an event chat will close
 * @param {Object} event - Event document with startsAt, endsAt
 * @returns {Date|null} - Date when chat closes, or null if not enabled
 */
export function getEventChatClosesAt(event) {
  if (!event?.groupChat?.enabled || !event.startsAt) {
    return null;
  }

  const startTime = event.startsAt?.toDate ? event.startsAt.toDate() : new Date(event.startsAt);
  const endTime = event.endsAt?.toDate ? event.endsAt.toDate() : startTime;
  return new Date(endTime.getTime() + 24 * 60 * 60 * 1000);
}

/**
 * Check if a group's chat is available
 * Group chats are always available if enabled
 * @param {Object} group - Group document with groupChat
 * @returns {boolean} - True if chat is available
 */
export function isGroupChatAvailable(group) {
  const enabled = group?.groupChat?.enabled;
  return enabled === true || enabled === 'true';
}

/**
 * Sort events by soonest first (for chat list)
 * @param {Array} events - Array of event objects
 * @returns {Array} - Sorted events
 */
export function sortEventsBySoonest(events) {
  return [...events].sort((a, b) => {
    const aStart = a.startsAt?.toDate ? a.startsAt.toDate() : new Date(a.startsAt);
    const bStart = b.startsAt?.toDate ? b.startsAt.toDate() : new Date(b.startsAt);
    return aStart.getTime() - bStart.getTime();
  });
}

/**
 * Sort groups by most recent activity (for chat list)
 * For now, we'll use createdAt. In the future, we could use lastMessageAt
 * @param {Array} groups - Array of group objects
 * @returns {Array} - Sorted groups
 */
export function sortGroupsByRecent(groups) {
  return [...groups].sort((a, b) => {
    const aTime = a.lastMessageAt?.toDate 
      ? a.lastMessageAt.toDate() 
      : (a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0));
    const bTime = b.lastMessageAt?.toDate 
      ? b.lastMessageAt.toDate() 
      : (b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0));
    return bTime.getTime() - aTime.getTime(); // Most recent first
  });
}

